import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { StylePicker, AIRewriteHeader, AIRewriteResult } from './airewrite/AIRewriteParts';
import { useAIRewrite } from './airewrite/useAIRewrite';

/**
 * <AIRewriteMenu> — global keyboard-triggered ("⌘.") AI rewrite popover.
 *
 * 1. User highlights text → presses ⌘.
 * 2. Popover appears near the selection with style chips (Shorten,
 *    Formalize, Casual, Summarize, Action items).
 * 3. Server returns rewritten text; "Replace" swaps it in-place
 *    (INPUT/TEXTAREA) or "Copy" puts it on the clipboard.
 *
 * Esc dismisses. Mounted once at the app root via App.js.
 * All behavior lives in `useAIRewrite` — this component only renders.
 */
export default function AIRewriteMenu() {
  const containerRef = useRef(null);
  const { snapshot, pos, rewritten, activeStyle, loading, close, run, replaceInPlace, copyResult } = useAIRewrite();

  if (!snapshot) return null;

  return createPortal(
    <div
      ref={containerRef}
      data-testid="ai-rewrite-menu"
      role="dialog"
      aria-label="AI rewrite menu"
      className="fixed z-[400] w-[320px] rounded-2xl overflow-hidden animate-fade-in"
      style={{
        top: pos.top,
        left: pos.left,
        background: 'var(--pd-surface-3)',
        border: '1px solid var(--pd-border-highlight)',
        boxShadow: 'var(--pd-shadow-command), var(--pd-edge-highlight)',
        backdropFilter: 'blur(28px) saturate(160%)',
        WebkitBackdropFilter: 'blur(28px) saturate(160%)',
      }}
    >
      <AIRewriteHeader onClose={close} />

      <div className="px-3 py-2">
        <div className="text-[10px] mb-2" style={{ color: 'var(--pd-text-tertiary)' }}>
          {snapshot.text.length > 80 ? `${snapshot.text.slice(0, 80)}…` : snapshot.text}
        </div>
        <StylePicker activeStyle={activeStyle} loading={loading} onPick={run} />
      </div>

      {loading ? (
        <div className="px-3 py-3 text-[11px]" style={{ color: 'var(--pd-text-tertiary)' }}>
          Rewriting…
        </div>
      ) : null}

      {rewritten && !loading ? (
        <AIRewriteResult
          rewritten={rewritten}
          snapshotKind={snapshot.kind}
          onReplace={replaceInPlace}
          onCopy={copyResult}
        />
      ) : null}
    </div>,
    document.body,
  );
}
